import type { ApiErrorBody } from '@kanban/shared';

/**
 * Fetch-Wrapper. Session-Cookie statt Token im LocalStorage (SPEC 3), daher
 * `credentials: 'include'` und ein CSRF-Header bei jeder Mutation.
 */

const CSRF_COOKIE = 'kanban_csrf';

/**
 * Zufaellige ID pro Browser-Tab. Sie geht bei jeder Mutation mit und wird beim
 * WebSocket-`subscribe` angemeldet, damit der Server den Verursacher vom
 * Broadcast ausnimmt (SPEC 7.1) — der hat den Stand durch sein optimistisches
 * Update bereits. Bewusst pro Tab und nicht pro Nutzer: sonst bekaeme das
 * zweite Geraet desselben Nutzers seine eigenen Aenderungen nicht.
 */
export const clientId: string =
  globalThis.crypto?.randomUUID?.() ?? `tab-${Math.random().toString(36).slice(2)}`;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }

  /** Feldfehler aus der Zod-Validierung, fuer die Anzeige am Formular. */
  get fields(): Record<string, string> {
    return this.body.fields ?? {};
  }
}

function readCsrfCookie(): string | null {
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : null;
}

/**
 * Das Token kommt primaer aus der Login-/me-Antwort; das Cookie ist der
 * Rueckfallweg nach einem Reload, bevor `me` beantwortet wurde.
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Offline wird gelesen, nicht geschrieben (SPEC 9.7, Akzeptanz A12).
 *
 * Die Sperre steht hier an der einen Stelle, durch die jede Mutation laeuft,
 * statt in jedem Formular. Sie wirft denselben `ApiError` wie der Server, also
 * greifen Toast-Anzeige und das Zuruecknehmen der optimistischen Updates ohne
 * Sonderbehandlung.
 *
 * Bewusst KEINE Warteschlange, obwohl SPEC 9.7 sie beschreibt: eine Aenderung,
 * die Stunden spaeter anlaeuft, trifft auf ein Board, das inzwischen jemand
 * anders umgeraeumt hat. Lieber ein ehrliches „geht gerade nicht" als ein
 * stiller Konflikt in der Nacht.
 */
function assertOnline(method: string): void {
  if (!MUTATING.has(method)) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError(503, {
      error: 'offline',
      message: 'Offline — Änderungen sind gerade nicht möglich',
    });
  }
}

/** Antwort auswerten: Fehler werfen, CSRF-Token mitnehmen, Rumpf zurueckgeben. */
async function readResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    throw new ApiError(response.status, (body ?? {
      error: 'unknown',
      message: 'Unbekannter Fehler',
    }) as ApiErrorBody);
  }

  if (body && typeof body === 'object' && 'csrfToken' in body) {
    csrfToken = (body as { csrfToken: string }).csrfToken;
  }

  return body as T;
}

/** Koepfe fuer eine Mutation: CSRF-Token und die Tab-Kennung fuer Realtime. */
function mutationHeaders(method: string): Record<string, string> {
  if (!MUTATING.has(method)) return {};
  const token = csrfToken ?? readCsrfCookie();
  return {
    ...(token ? { 'x-csrf-token': token } : {}),
    'x-client-id': clientId,
  };
}

/**
 * Datei hochladen (SPEC 9.8).
 *
 * Eigener Weg statt `request()`, weil `content-type` hier NICHT gesetzt werden
 * darf: den Rahmen (`boundary`) haengt der Browser selbst an, und ein von Hand
 * gesetzter Kopf ohne ihn laesst den Server den Rumpf nicht zerlegen.
 */
export async function upload<T>(path: string, file: File): Promise<T> {
  assertOnline('POST');

  const form = new FormData();
  form.append('datei', file);

  const response = await fetch(`/api/v1${path}`, {
    method: 'POST',
    headers: mutationHeaders('POST'),
    credentials: 'include',
    body: form,
  });

  return readResponse<T>(response);
}

async function request<T>(method: string, path: string, payload?: unknown): Promise<T> {
  assertOnline(method);

  const headers: Record<string, string> = { ...mutationHeaders(method) };
  if (payload !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: 'include',
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });

  return readResponse<T>(response);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, payload?: unknown) => request<T>('POST', path, payload),
  patch: <T>(path: string, payload?: unknown) => request<T>('PATCH', path, payload),
  // PUT fuer die Wiederholung: sie ersetzt die vorige vollstaendig, statt
  // Felder zu ergaenzen.
  put: <T>(path: string, payload?: unknown) => request<T>('PUT', path, payload),
  // Mit Rumpf, weil das Abmelden eines Geraets den Endpunkt mitschickt —
  // der ist lang und hat nichts in einer URL zu suchen.
  delete: <T>(path: string, payload?: unknown) => request<T>('DELETE', path, payload),
};
